import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { CalendarDays, TrendingUp, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";

interface MailingDataPoint {
  date: string;
  label: string;
  count: number;
}

export function MailingHistoryChart() {
  const [data, setData] = useState<MailingDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalMailed, setTotalMailed] = useState(0);
  const [uniqueDates, setUniqueDates] = useState(0);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const { data: rows, error } = await supabase
        .from("mailing_history")
        .select("mailed_date")
        .not("mailed_date", "is", null);

      if (error) {
        console.error("Error fetching mailing history:", error);
        setLoading(false);
        return;
      }

      // Aggregate by date
      const counts = new Map<string, number>();
      for (const row of rows || []) {
        const d = row.mailed_date as string;
        counts.set(d, (counts.get(d) || 0) + 1);
      }

      const sorted = Array.from(counts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, count]) => ({
          date,
          label: format(parseISO(date), "MMM d"),
          count,
        }));

      setData(sorted);
      setTotalMailed(rows?.length || 0);
      setUniqueDates(sorted.length);
      setLoading(false);
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading mailing history...</p>
        </CardContent>
      </Card>
    );
  }

  const avgPerMailing = uniqueDates > 0 ? Math.round(totalMailed / uniqueDates) : 0;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Mail className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{totalMailed.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Pieces Mailed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{uniqueDates}</p>
                <p className="text-xs text-muted-foreground">Mailing Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{avgPerMailing}</p>
                <p className="text-xs text-muted-foreground">Avg per Mailing</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bar chart */}
      <Card>
        <CardHeader>
          <CardTitle>Mailing Volume by Date</CardTitle>
          <CardDescription>Pieces mailed per day from mailing history</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                />
                <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload as MailingDataPoint;
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-md">
                        <p className="font-medium">{format(parseISO(d.date), "MMMM d, yyyy")}</p>
                        <p className="text-sm text-muted-foreground">{d.count.toLocaleString()} pieces mailed</p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Cumulative area chart */}
      <Card>
        <CardHeader>
          <CardTitle>Cumulative Mailings Over Time</CardTitle>
          <CardDescription>Running total of all pieces mailed</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data.reduce<(MailingDataPoint & { cumulative: number })[]>((acc, d) => {
                  const prev = acc.length > 0 ? acc[acc.length - 1].cumulative : 0;
                  acc.push({ ...d, cumulative: prev + d.count });
                  return acc;
                }, [])}
                margin={{ top: 10, right: 10, left: 0, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fontSize: 11 }}
                  className="fill-muted-foreground"
                />
                <YAxis tick={{ fontSize: 12 }} className="fill-muted-foreground" />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-md">
                        <p className="font-medium">{format(parseISO(d.date), "MMMM d, yyyy")}</p>
                        <p className="text-sm text-muted-foreground">{d.cumulative.toLocaleString()} total mailed</p>
                      </div>
                    );
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(var(--primary))"
                  fill="hsl(var(--primary) / 0.15)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
